import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import {
  resolveCliStrategyHealthFindingTarget,
  type CliStrategyHealthFinding,
  type CliStrategyHealthReport,
  type CliStrategyRepairData,
} from '@modules/shared-types';
import { StrategyHealthReportView } from '@modules/background-tasks';
import { STRATEGY_CANVAS } from '@test-kit/selectors/strategy-canvas';

export const StrategyHealthBadge: React.FC<{
  report?: CliStrategyHealthReport | null;
  onClick: () => void;
}> = ({ report, onClick }) => {
  const findings = report?.summary.findingsTotal;
  const errors = (report?.summary.bySeverity.error ?? 0) + (report?.summary.bySeverity.critical ?? 0);
  const color = !report ? 'default' : errors > 0 ? 'error' : findings ? 'warning' : 'success';
  const label = !report ? 'No health report' : findings ? `${findings} health findings` : '✓ Healthy';
  return (
    <Tooltip title="Open strategy health report" arrow>
      <Chip
        size="small"
        clickable
        variant={report ? 'filled' : 'outlined'}
        color={color}
        icon={<HealthAndSafetyIcon />}
        label={label}
        onClick={onClick}
        data-testid={STRATEGY_CANVAS.canvasHealthBadge}
      />
    </Tooltip>
  );
};

export interface StrategyHealthPanelProps {
  open: boolean;
  report?: CliStrategyHealthReport | null;
  refreshLoading?: boolean;
  onClose: () => void;
  onRefresh: () => Promise<void>;
  onFindingGo: (finding: CliStrategyHealthFinding) => void;
  onRepair?: (request: {
    blockId?: string;
    screenId?: string;
    apply?: boolean;
    approvedPlanChecksum?: `sha256:${string}`;
    resetAttemptLedger?: boolean;
  }) => Promise<CliStrategyRepairData>;
}

type RepairScope = { blockId?: string; screenId?: string };

export const StrategyHealthPanel: React.FC<StrategyHealthPanelProps> = ({
  open,
  report,
  refreshLoading = false,
  onClose,
  onRefresh,
  onFindingGo,
  onRepair,
}) => {
  const [repairScope, setRepairScope] = useState<RepairScope | null>(null);
  const [repairResult, setRepairResult] = useState<CliStrategyRepairData | null>(null);
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairError, setRepairError] = useState<string | null>(null);
  const [applyConfirmed, setApplyConfirmed] = useState(false);
  const [resetAttemptLedger, setResetAttemptLedger] = useState(false);
  const [resetConfirmed, setResetConfirmed] = useState(false);

  const checksum = repairResult?.report.planReceipt?.checksum;
  const scopeLabel = useMemo(() => {
    if (!repairScope?.blockId) return 'Whole strategy';
    if (!repairScope.screenId) return `Block ${repairScope.blockId}`;
    return `Screen ${repairScope.screenId} in block ${repairScope.blockId}`;
  }, [repairScope]);

  const chooseRepairScope = (scope: RepairScope) => {
    setRepairScope(scope);
    setRepairResult(null);
    setRepairError(null);
    setApplyConfirmed(false);
    setResetAttemptLedger(false);
    setResetConfirmed(false);
  };

  const chooseFindingRepair = (finding: CliStrategyHealthFinding) => {
    const target = resolveCliStrategyHealthFindingTarget(finding);
    if (!target.blockKey) {
      setRepairError('This finding has no owning block. Use whole-strategy repair.');
      return;
    }
    chooseRepairScope({
      blockId: target.blockKey,
      ...(target.screenId ? { screenId: target.screenId } : {}),
    });
  };

  const runRepair = async (apply: boolean) => {
    if (!onRepair || !repairScope || (apply && !checksum)) return;
    setRepairLoading(true);
    setRepairError(null);
    try {
      const result = await onRepair({
        ...repairScope,
        apply,
        resetAttemptLedger,
        ...(apply && checksum ? { approvedPlanChecksum: checksum } : {}),
      });
      setRepairResult(result);
      if (apply && result.report.status === 'completed') await onRefresh();
    } catch (error) {
      setRepairError((error as Error).message || 'Strategy repair failed.');
    } finally {
      setRepairLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Box
      data-testid={STRATEGY_CANVAS.canvasHealthPanel}
      sx={{
        position: 'absolute',
        zIndex: 20,
        top: 16,
        right: 16,
        width: 440,
        maxWidth: 'calc(100% - 32px)',
        maxHeight: 'calc(100% - 32px)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 8,
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 48, px: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <IconButton size="small" onClick={onClose} aria-label="Close strategy health" data-testid={STRATEGY_CANVAS.canvasHealthClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>Strategy Health</Typography>
        <Tooltip title="Run a fresh read-only health recomputation">
          <span>
            <IconButton
              size="small"
              disabled={refreshLoading}
              onClick={() => void onRefresh()}
              aria-label="Refresh strategy health"
              data-testid={STRATEGY_CANVAS.canvasHealthRefresh}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Box sx={{ p: 1.5, overflow: 'auto' }}>
        {onRepair && !repairScope && (
          <Button
            fullWidth
            variant="outlined"
            color="warning"
            onClick={() => chooseRepairScope({})}
            data-testid={STRATEGY_CANVAS.canvasRepairStrategy}
            sx={{ mb: 1.5 }}
          >
            Review &amp; repair whole strategy
          </Button>
        )}
        {report ? (
          <StrategyHealthReportView
            report={report}
            variant="canvas"
            onFindingGo={onFindingGo}
            onFindingRepair={onRepair ? chooseFindingRepair : undefined}
          />
        ) : (
          <Alert severity="info" variant="outlined">No report yet. Refresh to run a read-only verification.</Alert>
        )}
        {repairError && <Alert severity="error" sx={{ mt: 1 }} data-testid={STRATEGY_CANVAS.canvasRepairMessage}>{repairError}</Alert>}

        {repairScope && onRepair && (
          <Box
            data-testid={STRATEGY_CANVAS.canvasRepairPanel}
            sx={{ mt: 1.5, p: 1.25, border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2" sx={{ flex: 1 }}>Repair dry-run</Typography>
              <Button size="small" onClick={() => setRepairScope(null)} data-testid={STRATEGY_CANVAS.canvasRepairCancel}>Cancel</Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" data-testid={STRATEGY_CANVAS.canvasRepairScope}>
              Scope (locked for this receipt): {scopeLabel}
            </Typography>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={resetAttemptLedger}
                  disabled={Boolean(repairResult)}
                  onChange={(_, checked) => {
                    setResetAttemptLedger(checked);
                    setResetConfirmed(false);
                  }}
                  inputProps={{ 'data-testid': STRATEGY_CANVAS.canvasRepairResetLedger } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              )}
              label="Reset attempt ledger (advanced)"
            />
            {resetAttemptLedger && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Resetting the anti-oscillation ledger may repeat earlier model retries.
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={resetConfirmed}
                      onChange={(_, checked) => setResetConfirmed(checked)}
                      inputProps={{ 'data-testid': STRATEGY_CANVAS.canvasRepairResetConfirm } as React.InputHTMLAttributes<HTMLInputElement>}
                    />
                  )}
                  label="I confirm the exceptional reset"
                />
              </Alert>
            )}
            {!repairResult && (
              <Button
                fullWidth
                variant="contained"
                color="warning"
                disabled={repairLoading || (resetAttemptLedger && !resetConfirmed)}
                onClick={() => void runRepair(false)}
                data-testid={STRATEGY_CANVAS.canvasRepairDryRun}
              >
                {repairLoading ? 'Planning…' : 'Generate dry-run plan'}
              </Button>
            )}
            {repairResult && (
              <Stack spacing={1} sx={{ mt: 1 }}>
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip size="small" label={`${repairResult.report.planSummary?.autofixes ?? 0} autofixes`} />
                  <Chip size="small" label={`${repairResult.report.planSummary?.retries ?? 0} retries`} />
                  <Chip size="small" label={`${repairResult.report.planSummary?.surfaced ?? 0} surfaced`} />
                </Stack>
                {repairResult.report.plannedActions?.slice(0, 8).map((action, index) => (
                  <Typography key={`${action.kind}-${index}`} variant="caption" color="text.secondary">
                    {action.kind}{action.fix ? ` · ${action.fix}` : ''}
                    {action.target.screenId ? ` · screen ${action.target.screenId}` : action.target.blockKey ? ` · block ${action.target.blockKey}` : ''}
                  </Typography>
                ))}
                {checksum ? (
                  <>
                    <Divider />
                    <Typography variant="caption">Fresh plan checksum</Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                      data-testid={STRATEGY_CANVAS.canvasRepairChecksum}
                    >
                      {checksum}
                    </Typography>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={applyConfirmed}
                          onChange={(_, checked) => setApplyConfirmed(checked)}
                          inputProps={{ 'data-testid': STRATEGY_CANVAS.canvasRepairConfirm } as React.InputHTMLAttributes<HTMLInputElement>}
                        />
                      )}
                      label="Apply this exact content-addressed plan"
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      color="warning"
                      disabled={!applyConfirmed || repairLoading || (resetAttemptLedger && !resetConfirmed)}
                      onClick={() => void runRepair(true)}
                      data-testid={STRATEGY_CANVAS.canvasRepairApply}
                    >
                      {repairLoading ? 'Applying…' : 'Apply approved plan'}
                    </Button>
                  </>
                ) : (
                  <Alert severity={repairResult.report.status === 'refused' ? 'warning' : 'error'}>
                    {repairResult.report.refusal?.code || repairResult.report.error || 'The dry-run did not return an approval receipt.'}
                  </Alert>
                )}
                {repairResult.report.planApproval && (
                  <Alert severity="success" data-testid={STRATEGY_CANVAS.canvasRepairMessage}>
                    Applied checksum {repairResult.report.planApproval.approvedPlanChecksum}; approval matched.
                  </Alert>
                )}
              </Stack>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
